import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useAuth } from "hooks/useAuth";
import useMfaStatus from "hooks/useMfaStatus";
import DashboardLayout from "components/dashboard/DashboardLayout";
import DashboardRoutes from "routes/DashboardRoutes";
import PatientRoutes from "routes/PatientRoutes";
import LearnerRoutes from "routes/LearnerRoutes";
import BtybMarketerRoutes from "routes/BtybMarketerRoutes";
import FusionAffiliateRoutes from "routes/FusionAffiliateRoutes";
import ProfessionalRoutes from "routes/ProfessionalRoutes";
import AdminRoutes from "routes/AdminRoutes";
import GlobalAdminRoutes from "routes/GlobalAdminRoutes";
import RegionalAdminRoutes from "routes/RegionalAdminRoutes";
import { ChatProvider } from "context/Chat";
import { db } from "services/firebase";
import Loading from "components/Loading";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { roleRequiresMfa } from "utils/mfaPolicy";
import { isEmailVerifiedForApp } from "utils/isDevAuthFirestoreUser";

// Pre-shell gate: do not wrap ColorModeThemeProvider here. Mounting it for the
// spinner and then swapping to DashboardNav's provider (CssBaseline + dark
// GlobalStyles) can throw insertBefore on the style tag and wipe the page.
// Give the box real height — Loading(page) is position:absolute, so a zero-height
// parent collapses the spinner into a blank white screen.
const DashboardLoading = () => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      minHeight: "100dvh",
      display: "flex",
      justifyContent: "center",
      overflow: "hidden",
    }}
  >
    <Loading page />
  </Box>
);

const DashboardPage = () => {
  const { user, userLoading, userData, isGlobalAdmin } = useAuth();
  const { isEnrolled, sessionMfaLoading } = useMfaStatus();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userDocChecked, setUserDocChecked] = useState(false);

  // Effect to handle authentication and data loading
  useEffect(() => {
    const checkUserStatus = async () => {
      // Wait for authentication to complete
      if (userLoading) return;

      // If no user, we'll handle redirect in the render
      if (!user) {
        setIsLoading(false);
        setUserDocChecked(true);
        return;
      }

      // If userData is already loaded through useAuth hook
      if (userData) {
        setIsLoading(false);
        setUserDocChecked(true);
        return;
      }

      // If we have a user but no userData, check Firestore directly
      try {
        const userDoc = await db.collection("users").doc(user.uid).get();
        const userData = userDoc.data();

        if (!userDoc.exists) {
          // Redirect to onboarding if no user document
          navigate("/onboarding");
        } else if (userData.onboarding) {
          // If user is still in onboarding, redirect back to onboarding
          navigate("/onboarding");
        } else {
          // We've confirmed the document exists and onboarding is complete
          setIsLoading(false);
          setUserDocChecked(true);
        }
      } catch (error) {
        console.error("Error checking user document:", error);
        setIsLoading(false);
        setUserDocChecked(true);
      }
    };

    checkUserStatus();
  }, [user, userLoading, userData]);

  // If still loading or user document hasn't been checked, show loading screen
  if (isLoading || !userDocChecked) {
    return <DashboardLoading />;
  }

  // If user is not authenticated, redirect to login.
  //
  // An anonymous user counts as not authenticated. They are a guest on a public
  // page (CareStart, VisitStart) and have no user doc, so without this they fall
  // through to the missing-doc branch, get pushed to /onboarding, and are bounced
  // from there to /auth's "verify your email" step for an account with no email.
  if (!user || user.isAnonymous) {
    return <Navigate to="/auth" />;
  }

  // Workforce roles are held at onboarding until MFA is enrolled; patients are
  // exempt (roleRequiresMfa fails closed for any non-patient role).
  if (
    !sessionMfaLoading &&
    !isEnrolled &&
    !shouldSkipMfaEnrollmentUi() &&
    roleRequiresMfa(userData?.role)
  ) {
    return <Navigate to="/onboarding" />;
  }

  if (sessionMfaLoading) {
    return <DashboardLoading />;
  }

  // Safety-net: unverified email users who slipped past the AuthFlow gate.
  // @dev.local Dev Sign In accounts are localhost-only and count as verified.
  if (!isEmailVerifiedForApp(user, userData)) {
    const resend = async () => {
      try {
        await firebase.auth().currentUser.sendEmailVerification();
      } catch (e) {
        /* ignore */
      }
    };
    return (
      <DashboardLayout>
        <Typography variant="h5" sx={{ mt: { xs: 4, sm: 8 }, mb: 2 }}>
          Confirm your email
        </Typography>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please verify your email address before continuing. Check your inbox
          for a link from us.
        </Alert>
        <Button variant="contained" onClick={resend}>
          Resend Verification Email
        </Button>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Need help? Email{" "}
            <a href="mailto:support@md3c.com">support@md3c.com</a>
          </Typography>
        </Box>
      </DashboardLayout>
    );
  }

  // Facility users awaiting admin approval of their access request have no
  // facility membership yet, so the dashboard is an empty shell — hold them on
  // the onboarding pending screen until an admin reviews the claim. Rejected
  // claimants are in the same boat (role "facility", no membership), so route
  // them to onboarding too, which shows a "request rejected" screen.
  if (userData?.facilityClaimPending || userData?.facilityClaimRejected) {
    return <Navigate to="/onboarding" />;
  }

  // If we have userData but no role, redirect to onboarding
  if (userData && !userData.role) {
    navigate("/onboarding");
    return <DashboardLoading />;
  }

  // A provider who came from a /share link to sign in lands here once fully
  // authed; bounce them back to the share page (now signed in) so they can view
  // the record and push it into ChartMind. The key is cleared on the /share side
  // (ShareLandingPage mount) so this render stays pure.
  const shareReturn = localStorage.getItem("pendingShareReturn");
  // /discharge too: a patient who scanned a discharge QR as a guest and chose "sign in"
  // comes back to that page, which moves the instructions into the account.
  if (
    shareReturn &&
    (shareReturn.startsWith("/share") || shareReturn.startsWith("/discharge"))
  ) {
    return <Navigate to={shareReturn} replace />;
  }

  return (
    <ChatProvider>
      {isGlobalAdmin ? (
        <GlobalAdminRoutes />
      ) : userData?.role === "regional_admin" ? (
        String(userData?.region || "").trim() ? (
          <RegionalAdminRoutes />
        ) : (
          <DashboardLayout>
            <Alert severity="warning" sx={{ m: 2 }}>
              Your account is missing <strong>region</strong> on your user
              document. An administrator must set it to match regional prompt
              scope (any stable string).
            </Alert>
          </DashboardLayout>
        )
      ) : userData?.role === "learner" ? (
        <LearnerRoutes />
      ) : userData?.role === "btybMarketer" ? (
        <BtybMarketerRoutes />
      ) : userData?.role === "fusionAffiliate" ? (
        <FusionAffiliateRoutes />
      ) : userData?.role === "patient" ? (
        <PatientRoutes />
      ) : userData?.role === "professional" ? (
        <ProfessionalRoutes />
      ) : userData?.role === "admin" ? (
        <AdminRoutes />
      ) : (
        <DashboardRoutes />
      )}
    </ChatProvider>
  );
};

export default DashboardPage;
